import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
  FormControl,
  Grid,
  Paper,
  Popper,
  ListItem,
  List,
  FormHelperText,
  debounce,
  Link,
  InputLabel,
  MenuItem,
  Select,
} from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../core/store';
import { authService } from '../../core/services/auth.service';
import { Company } from '../../core/services/company.service';
import {
  databaseService,
  Database,
} from '../../core/services/database.service';
import { ROUTES } from '../../core/constants';
import { Errors } from 'core/types/Generic';
import SearchIcon from '@mui/icons-material/Search';
import userService from 'core/services/user.service';
import logger from 'core/utils/logger';
import { getAuditLogsApiFromToken } from 'core/utils/jwt';
import { logAppEntered } from 'core/amplitude/amplitude';
import { useSnackbar } from 'notistack';
import packageJson from '../../../package.json';
// Company and Database interfaces are now imported from services

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const { login, setLoading } = useAuthStore();

  const [formData, setFormData] = useState({
    company: '',
    database: '',
    username: '',
    password: '',
  });
  const [companies, setCompanies] = useState<Company[]>([]);
  const [databases, setDatabases] = useState<Database[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [selectedDatabase, setSelectedDatabase] = useState<Database | null>(
    null
  );
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [loadingDatabases, setLoadingDatabases] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);
  const [errors, setErrors] = useState<Errors>({});
  const [genSearchVal, setGenSearchVal] = useState<string | null>('');
  const [open, setOpen] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  // Load databases when company is selected
  useEffect(() => {
    if (selectedCompany) {
      loadDatabases(selectedCompany.companyIdentity);
    } else {
      setDatabases([]);
      setSelectedDatabase(null);
    }
  }, [selectedCompany]);

  const loadDatabases = async (companyId: string) => {
    try {
      setLoadingDatabases(true);
      const databasesData =
        await databaseService.getDatabasesByCompany(companyId);
      setDatabases(databasesData);
      logger.info('Databases loaded', {
        companyId,
        count: databasesData.length,
      });
    } catch (error) {
      logger.error('Failed to load databases', { companyId }, error as Error);
      setError('Failed to load databases');
    } finally {
      setLoadingDatabases(false);
    }
  };

  const handleCompanyChange = (newValue: Company | null) => {
    setSelectedCompany(newValue);
    setFormData(prev => ({
      ...prev,
      company: newValue?.companyName || '',
    }));
    // Update the search input to show the selected company name
    setGenSearchVal(newValue?.companyName || '');
    setError('');
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // eslint-disable-next-line no-debugger
    //debugger;
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
    if (error) setError('');
  };

  const validateForm = () => {
    const newErrors: Errors = {};

    if (!selectedCompany) newErrors.selectedCompany = 'Company is required.';
    if (!selectedDatabase) newErrors.selectedDatabase = 'Database is required.';
    if (!formData.username.trim()) newErrors.username = 'Username is required.';
    if (!formData.password.trim()) newErrors.password = 'Password is required.';

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (
      !selectedCompany ||
      !selectedDatabase ||
      !formData.username ||
      !formData.password
    ) {
      validateForm();
      return;
    }

    setIsLoading(true);
    setLoading(true);

    try {
      logger.info('Login attempt', {
        company: selectedCompany.companyName,
        database: selectedDatabase.databaseName,
        username: formData.username,
      });

      // Create login request with company and database info
      const loginRequest = {
        companyIdentity: selectedCompany.companyIdentity,
        databaseIdentity: selectedDatabase.databaseIdentity,
        username: formData.username,
        password: formData.password,
        requestFor: 'aspen',
      };

      const response = await authService.login(loginRequest);

      // Store tokens and company/database info
      localStorage.setItem('auth_token', response.token);
      localStorage.setItem('refresh_token', response.refreshToken);
      localStorage.setItem('selected_company', JSON.stringify(selectedCompany));
      localStorage.setItem(
        'selected_database',
        JSON.stringify(selectedDatabase)
      );
      // eslint-disable-next-line no-debugger
      //debugger;
      // Store backend service URL from JWT token if available
      const auditLogsApi = getAuditLogsApiFromToken(response.token);
      if (auditLogsApi) {
        localStorage.setItem('auditLogsApi', auditLogsApi);
        logger.info('Backend service URL stored from JWT token', {
          backendServiceUrl: auditLogsApi,
        });
      } else {
        setError('Backend service URL not found. Please contact your manager.');
        return;
      }

      const userInfo = await userService.getUserByUserName(formData.username);
      if (!userInfo.hasAuditLogAccess) {
        // enqueueSnackbar(
        //   "You don't have permission. Please contact your manager.",
        //   {
        //     variant: 'error',
        //   }
        // );
        setError("You don't have permission. Please contact your manager.");
        return;
      }

      // Update store
      userInfo.company = selectedCompany.companyName;
      userInfo.userNameAndCompany = `${userInfo.userName}@${userInfo.company}`;
      login(userInfo, response.token);
      localStorage.setItem('user', JSON.stringify(userInfo));
      // Track app entry for analytics (web login)
      logAppEntered(
        userInfo.userNameAndCompany,
        userInfo.company,
        'web',
        packageJson.version
      );

      logger.info('Login successful', {
        userId: response,
        company: selectedCompany?.companyName,
        database: selectedDatabase?.databaseName,
      });

      // Navigate to dashboard
      navigate(ROUTES.AUDIT);
    } catch (error) {
      setError('Login failed');
    } finally {
      setIsLoading(false);
      setLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const fetchCompanies = async (query: string) => {
    setLoadingCompanies(true);
    try {
      const companyList = await authService.getCompany(query);
      if (companyList) setCompanies(companyList as Company[]);
    } catch (error) {
      //console.error('Error fetching customers:', error);
      setError("You don't have permission. Please contact your manager.");

      navigate('/error');
    } finally {
      setLoadingCompanies(false);
    }
    //logAmplitudeEvent("Fetching companies", "fetchCompanies-Login");
  };

  const debouncedFetchCompanies = useCallback(
    debounce(query => {
      if (query.length >= 3) {
        fetchCompanies(query);
      } else {
        setCompanies([]);
        setDatabases([]);
        setSelectedCompany(null);
        setSelectedDatabase(null);
      }
    }, 500),
    []
  );

  const handleCompanySearch = (event: any) => {
    const query = event.target.value;
    setGenSearchVal(event.target.value);
    debouncedFetchCompanies(query);
    //logAmplitudeEvent("Company Search", "handleCompanySearch-Login");
  };
  const [loading] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const handleResetCache = async () => {
    setIsResetting(true);

    try {
      // Clear localStorage & sessionStorage
      localStorage.clear();
      sessionStorage.clear();

      // Clear IndexedDB (optional, async)
      const databases = await indexedDB.databases();
      databases.forEach(db => {
        if (db.name) indexedDB.deleteDatabase(db.name);
      });

      // Simulating a short delay
      await new Promise(resolve => setTimeout(resolve, 2000));

      //alert("Cache reset successfully! Please refresh the page.");
      // Swal.fire({
      //   title: "Success",
      //   html: `Cache reset successfully! Please refresh the page.`,
      //   icon: "success",
      //   confirmButtonColor: "#2371E7",
      //   confirmButtonText: "Ok",
      //   allowOutsideClick: false,
      // });
    } catch (error) {
      // console.error("Error resetting cache:", error);
      // alert("Failed to reset cache. Please try again.");
      // Swal.fire({
      //   title: "Error",
      //   html: `Resetting Cache Failed`,
      //   icon: "error",
      //   confirmButtonColor: "#2371E7",
      //   confirmButtonText: "OK",
      //   allowOutsideClick: false,
      // });
    } finally {
      setIsResetting(false);
    }
  };
  return (
    <Box
      sx={{
        height: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f9f9f9',
      }}
    >
      <Box
        sx={{
          width: '45%',
          boxShadow: 3,
          backgroundColor: '#fff',
          borderRadius: 2,
          overflow: 'hidden',
        }}
      >
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <Grid container>
          {/* Left Column */}
          <Grid
            item
            xs={12}
            md={5}
            sx={{
              backgroundColor: '#181F2A',
              padding: 4,
              display: 'flex',

              flexDirection: 'column',
              justifyContent: 'space-between',
              alignItems: 'center',
              textAlign: 'center',
              borderRight: '1px solid #e0e0e0',
            }}
          >
            {/* Header Section */}
            <Box>
              <Typography
                color="white"
                variant="h4"
                fontWeight="bold"
                gutterBottom
              >
                Login
              </Typography>
              <Typography color="white" variant="body1">
                Welcome back! <br /> Please enter your details.
              </Typography>
            </Box>

            {/* Footer Section */}
            <Box>
              <Typography variant="body2" sx={{ color: '', marginBottom: 2 }}>
                <Link
                  href=""
                  target="_blank"
                  rel="noopener noreferrer"
                  underline="hover"
                  color="primary"
                >
                  Version {packageJson.version}
                </Link>
              </Typography>
              <Box
                component="img"
                src="/aspen-logo-normal-white.png"
                alt="Aspen Logo"
                sx={{ width: 150 }}
              />
            </Box>
          </Grid>

          {/* Right Column */}
          <Grid item xs={12} md={7} sx={{ padding: 4 }}>
            <Box
              component="form"
              onSubmit={handleSubmit}
              sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}
            >
              <Box>
                {/* Company Selection */}
                <FormControl
                  error={!!errors.selectedCompany}
                  size="small"
                  variant="outlined"
                  ref={anchorRef}
                  fullWidth
                >
                  <TextField
                    fullWidth
                    label="Company"
                    variant="outlined"
                    size="small"
                    onChange={event => {
                      handleCompanySearch(event);
                      setOpen(true);
                    }}
                    onBlur={() => setTimeout(() => setOpen(false), 200)} // Delay to allow click selection
                    onFocus={() => setOpen(companies.length > 0)}
                    name="GENERIC_SEARCH"
                    value={genSearchVal}
                    placeholder="Search Company"
                    InputProps={{
                      endAdornment: loading ? (
                        <CircularProgress size={20} />
                      ) : (
                        <SearchIcon />
                      ),
                    }}
                    sx={{
                      minWidth: '10%',
                      '& .MuiInputBase-input::placeholder': {
                        textAlign: 'left',
                      },
                    }}
                    // inputRef={anchorRef} // Attach ref for positioning
                  />

                  <Popper
                    open={open && companies.length > 0}
                    anchorEl={anchorRef.current}
                    placement="bottom-start"
                    modifiers={[
                      {
                        name: 'preventOverflow',
                        options: {
                          boundary: 'window', // Prevents overlapping with other elements
                        },
                      },
                      {
                        name: 'offset',
                        options: {
                          offset: [0, 2], // Adds space between the field and list
                        },
                      },
                    ]}
                    sx={{
                      zIndex: 1300, // Ensures dropdown appears above other elements
                      width: anchorRef.current
                        ? `${anchorRef.current.offsetWidth}px`
                        : 'auto', // Dynamic width
                    }}
                  >
                    <Paper
                      sx={{
                        width: '100%', // <-- Ensures full width
                        maxHeight: 200,
                        overflowY: 'auto',
                        backgroundColor: 'white', // Ensure background remains white
                        borderRadius: '8px', // Rounded corners
                        boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.15)', // Add shadow for depth
                        border: '1px solid #ddd', // Light border for separation
                        //mt: 1, // Small margin to prevent sticking to TextField
                      }}
                    >
                      <List>
                        {companies.map(company => (
                          <ListItem
                            key={company.companyIdentity}
                            onClick={() => {
                              handleCompanyChange(company);
                              setOpen(false);
                            }}
                            sx={{
                              cursor: 'pointer',
                              padding: '8px',

                              '&:hover': { backgroundColor: '#f0f0f0' },
                            }}
                          >
                            {company.companyName}
                          </ListItem>
                        ))}
                      </List>
                    </Paper>
                  </Popper>

                  <FormHelperText>{errors.selectedCompany}</FormHelperText>
                </FormControl>
              </Box>
              {/* Database Selection */}
              <FormControl
                error={!!errors.selectedDatabase}
                size="small"
                variant="outlined"
                fullWidth
              >
                <InputLabel id="database-label">Database</InputLabel>
                <Select
                  disabled={!databases.length}
                  labelId="database-label"
                  id="database"
                  fullWidth
                  size="small"
                  label="Company"
                  placeholder="Enter your company name"
                  variant="outlined"
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: 200, // Set dropdown height
                        overflowY: 'auto', // Add scrollbar if needed
                      },
                    },
                  }}
                  value={selectedDatabase?.databaseIdentity ?? ''}
                  onChange={(e: SelectChangeEvent<string>) => {
                    const selectedId = e.target.value as string;
                    const db = databases.find(
                      d => d.databaseIdentity === selectedId
                    );
                    setSelectedDatabase(db ?? null);
                  }}
                >
                  {databases.map(db => (
                    <MenuItem
                      key={db.databaseIdentity}
                      value={db.databaseIdentity}
                    >
                      {db.databaseName}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>{errors.selectedDatabase}</FormHelperText>
              </FormControl>

              {/* Username */}
              <TextField
                id="username"
                label="Username"
                variant="outlined"
                size="small"
                type="text"
                name="username"
                value={formData.username}
                onChange={handleInputChange}
                error={!!errors.username}
                helperText={errors.username}
              />

              {/* Password */}
              <TextField
                id="password"
                size="small"
                label="Password"
                variant="outlined"
                type={showPassword ? 'text' : 'password'}
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                error={!!errors.password}
                helperText={errors.password}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        id="passwordVisibilityBtn"
                        onClick={togglePasswordVisibility}
                      >
                        {showPassword ? <Visibility /> : <VisibilityOff />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              {/* Login Button bottom*/}
              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="small"
                sx={{
                  mt: 2,
                  textTransform: 'capitalize',
                  backgroundColor: '#1976d2',
                }}
                disabled={
                  !selectedCompany ||
                  !selectedDatabase ||
                  !formData.username ||
                  !formData.password ||
                  isLoading
                }
              >
                {isLoading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  'Login'
                )}
              </Button>

              <Box mt={1} sx={{ textAlign: 'right' }}>
                {isResetting ? (
                  <CircularProgress size={20} />
                ) : (
                  <Typography
                    variant="body2"
                    color="primary"
                    sx={{ cursor: 'pointer', textDecoration: 'underline' }}
                    onClick={handleResetCache}
                  >
                    {/* Having issues? [Reset Cache] */}
                  </Typography>
                )}
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default LoginPage;
